import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Moon, Sun, Menu, X } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage, AVAILABLE_LANGUAGES, LANGUAGES } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import OptimizedImage from '../ui/OptimizedImage';

interface PublicLayoutProps {
  children: React.ReactNode;
}

const PublicLayout: React.FC<PublicLayoutProps> = ({ children }) => {
  const { t, language, setLanguage, getLanguageInfo } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex flex-col">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 shadow-lg' 
          : 'bg-white/95 dark:bg-slate-950/95 backdrop-blur-md border-b border-slate-200/50 dark:border-slate-800/50 shadow-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo & Navigation */}
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center group">
                <img
                  src="/logo.png"
                  alt="Revomira"
                  className="h-16 w-auto object-contain transition-transform duration-200 group-hover:scale-105"
                />
              </Link>

              {/* Navigation Links - Desktop */}
              <div className="hidden lg:flex items-center gap-3">
                <Link
                  to="/about"
                  className="text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  {t('nav.about')}
                </Link>
                <span className="text-slate-300 dark:text-slate-600">|</span>
                <Link
                  to="/contact"
                  className="text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  {t('nav.contact')}
                </Link>
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-3">
              {/* Language Selector */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                  className="h-10 px-3 flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <OptimizedImage 
                    src={getLanguageInfo().flag} 
                    alt={getLanguageInfo().name} 
                    width={20}
                    height={20}
                    className="w-5 h-5 object-contain rounded" 
                  />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200 hidden sm:inline">
                    {getLanguageInfo().nativeName}
                  </span>
                </Button>
                {isLanguageOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsLanguageOpen(false)} />
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-900 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
                      {AVAILABLE_LANGUAGES.map((lang) => {
                        const langInfo = LANGUAGES[lang];
                        return (
                          <button
                            key={lang}
                            onClick={() => {
                              setLanguage(lang);
                              setIsLanguageOpen(false);
                            }}
                            className={`w-full text-left px-4 py-3 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-3 transition-colors ${
                              language === lang 
                                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium' 
                                : 'text-slate-700 dark:text-slate-300'
                            }`}
                          >
                            <OptimizedImage 
                              src={langInfo.flag} 
                              alt={langInfo.name} 
                              width={20}
                              height={20}
                              className="w-5 h-5 object-contain rounded" 
                            />
                            <span>{langInfo.nativeName}</span>
                            {language === lang && (
                              <span className="ml-auto text-blue-600 dark:text-blue-400">✓</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className="h-10 w-10 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                aria-label={t('topbar.themeToggle')}
              >
                {theme === 'dark' ? (
                  <Sun className="h-5 w-5 text-amber-400" />
                ) : (
                  <Moon className="h-5 w-5 text-slate-600" />
                )}
              </Button>

              {/* Auth Buttons — desktop */}
              <div className="hidden sm:flex items-center gap-2 ml-2">
                <Button
                  variant="ghost"
                  asChild
                  className="h-11 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors leading-normal"
                >
                  <Link to="/login" className="flex items-center">{t('nav.signIn')}</Link>
                </Button>
                <Button asChild className="h-11 px-6 py-2.5 text-sm font-semibold leading-normal">
                  <Link to="/register">{t('nav.getStarted')}</Link>
                </Button>
              </div>

              {/* Hamburger — mobile only */}
              <Button
                variant="ghost"
                size="sm"
                className="sm:hidden h-10 w-10 p-0"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile menu panel */}
        {isMobileMenuOpen && (
          <div className="sm:hidden border-t border-border bg-background px-4 pb-4 pt-3 space-y-1">
            <Link
              to="/about"
              className="block py-2.5 px-3 rounded-md text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-secondary/60 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('nav.about')}
            </Link>
            <Link
              to="/contact"
              className="block py-2.5 px-3 rounded-md text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-secondary/60 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('nav.contact')}
            </Link>
            <div className="flex flex-col gap-2 pt-3 border-t border-border mt-2">
              <Button variant="outline" asChild className="w-full justify-center">
                <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>{t('nav.signIn')}</Link>
              </Button>
              <Button asChild className="w-full justify-center">
                <Link to="/register" onClick={() => setIsMobileMenuOpen(false)}>{t('nav.getStarted')}</Link>
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-1 pt-20">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 dark:bg-slate-950 text-white py-8 px-4 sm:px-6 lg:px-8 mt-auto">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <div className="flex items-center mb-2">
                <img
                  src="/logo.png"
                  alt="Revomira"
                  className="h-20 w-auto object-contain"
                />
              </div>
              <p className="text-slate-400 text-sm max-w-md">
                {t('footer.description')}
              </p>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <Link to="/about" className="text-slate-400 hover:text-white transition-colors">
                {t('nav.about')}
              </Link>
              <Link to="/contact" className="text-slate-400 hover:text-white transition-colors">
                {t('nav.contact')}
              </Link>
              <Link to="/privacy" className="text-slate-400 hover:text-white transition-colors">
                {t('footer.privacy')}
              </Link>
              <Link to="/terms" className="text-slate-400 hover:text-white transition-colors">
                {t('footer.terms')}
              </Link>
              <Link to="/cookies" className="text-slate-400 hover:text-white transition-colors">
                {t('footer.cookies')}
              </Link>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-400 text-sm">
            <p>{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;

